import Loader from "components/DesignSystem/Loader/loader";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import Slider from "components/DesignSystem/Slider/Slider";
import { TableUI } from "components/design/TableUI";
import { PersonaIcon } from "components/icons/PersonaIcon";
import { EmptyStateWindIcon } from "components/icons/EmptyStateWindIcon";
import { MissingInfoWarningIcon } from "components/icons/MissingInfoWarningIcon";
import { SmallChevronToggleIcon } from "components/icons/SmallChevronToggleIcon";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getCoreRowModel, Row, useReactTable } from "react-table-8.10.7";
import {
  useGetEntityAPDetailsQuery,
  useGetPeopleForEntityQuery,
  useUpdateEntityAPDetailsMutation,
} from "store/apis/directorsAndOfficers";
import { useGetAllShareholdersQuery } from "store/apis/Shareholders";
import { Person } from "types/Models/apAutofills";
import { BackendError } from "types/utils/error";
import { AddPersonModal } from "./AddPersonModal";
import {
  ConfirmNoDirectorOfficerModal,
  ConfirmNoDirectorOfficerVariant,
} from "./ConfirmNoDirectorOfficerModal";
import { mergePersonAndShareholder } from "./personDetailsUtils";
import { NoAuthorisedPersonaTypeExist } from "../NoAuthorisedPersonaTypeExist";
import { PersonSlider } from "./PersonSlider";
import {
  countryColumn,
  createActionColumn,
  nameColumn,
  rolesColumn,
  statusColumn,
} from "./peopleTableColumns";

const EMPTY_PEOPLE: Person[] = [];

export const PeopleTable = ({ onAddPerson }: { onAddPerson: () => void }) => {
  const { entityId } = useParams<{ entityId: string }>();
  const { uuid: groupId } = useCurrentGroupContext();
  const { alertToast, successToast } = useToast();
  const [openSlider, setOpenSlider] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);
  const [confirmVariant, setConfirmVariant] =
    useState<ConfirmNoDirectorOfficerVariant | null>(null);
  const [showFormer, setShowFormer] = useState(false);

  const { data, isLoading, isSuccess, isError } = useGetPeopleForEntityQuery(
    { entityId },
    { skip: !entityId }
  );
  const { data: entityAPDetails } = useGetEntityAPDetailsQuery(
    { entityId },
    { skip: !entityId }
  );
  const { data: shareholders = [] } = useGetAllShareholdersQuery(
    { entityId, groupId },
    { skip: !groupId || !entityId }
  );
  const [updateEntityAPDetails] = useUpdateEntityAPDetailsMutation();

  const people = useMemo(
    () =>
      [...(data?.people || [])].sort(
        (a, b) =>
          Number(b.is_authorised_signatory) - Number(a.is_authorised_signatory)
      ),
    [data]
  );
  const formerPeople = data?.former_people ?? EMPTY_PEOPLE;

  const openPersonSlider = (person: Person) => {
    setSelectedPersonId(person.persona_autofill_id);
    setOpenSlider(true);
  };

  const shareholderForPersona = (personaAutofillId: string) =>
    shareholders.find(
      (shareholder) =>
        shareholder.shareholder_type === "PERSONA" &&
        shareholder.instance_id === personaAutofillId
    ) || null;

  const editingShareholder = editingPerson
    ? shareholderForPersona(editingPerson.persona_autofill_id)
    : null;
  const editingPersonWithShareholder = editingPerson
    ? mergePersonAndShareholder(editingPerson, editingShareholder)
    : null;

  const actionColumn = useMemo(
    () =>
      createActionColumn({
        onViewDetails: (person) => openPersonSlider(person),
        onEdit: (person) => setEditingPerson(person),
      }),
    []
  );

  const activeColumns = useMemo(
    () => [
      nameColumn,
      rolesColumn("active"),
      countryColumn,
      statusColumn("active"),
      actionColumn,
    ],
    [actionColumn]
  );
  const formerColumns = useMemo(
    () => [
      nameColumn,
      rolesColumn("former"),
      countryColumn,
      statusColumn("former"),
      actionColumn,
    ],
    [actionColumn]
  );

  const activeTable = useReactTable({
    columns: activeColumns,
    getCoreRowModel: getCoreRowModel(),
    data: people,
    defaultColumn: { size: 10, minSize: 1, maxSize: 100 },
  });
  const formerTable = useReactTable({
    columns: formerColumns,
    getCoreRowModel: getCoreRowModel(),
    data: formerPeople,
    defaultColumn: { size: 10, minSize: 1, maxSize: 100 },
  });

  const rawSelectedPerson = [...people, ...formerPeople].find(
    ({ persona_autofill_id }) => persona_autofill_id === selectedPersonId
  );
  const selectedPerson = rawSelectedPerson
    ? mergePersonAndShareholder(
        rawSelectedPerson,
        shareholderForPersona(rawSelectedPerson.persona_autofill_id)
      )
    : null;

  const onRowClick = (row: Row<Person>) => {
    openPersonSlider(row.original);
  };

  const allPeople = [...people, ...formerPeople];
  const hasDirectorRole = allPeople.some((person) =>
    person.roles.some((role) => role.persona_type === "DIRECTOR")
  );
  const hasOfficerRole = allPeople.some((person) =>
    person.roles.some((role) => role.persona_type === "OFFICER")
  );

  const handleConfirmNone = async (
    variant: ConfirmNoDirectorOfficerVariant
  ) => {
    try {
      if (entityAPDetails) {
        await updateEntityAPDetails({
          payload: {
            ...entityAPDetails,
            ...(variant !== "officer" && { director_exists: false }),
            ...(variant !== "director" && { officer_exists: false }),
          },
          entityId,
        }).unwrap();
        successToast({ message: "Status updated." });
      } else {
        onAddPerson();
      }
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const isEmpty = people.length === 0 && formerPeople.length === 0;

  const showMissingDirectorsBanner =
    !isEmpty && !hasDirectorRole && entityAPDetails?.director_exists !== false;
  const showMissingOfficersBanner =
    !isEmpty && !hasOfficerRole && entityAPDetails?.officer_exists !== false;
  const directorConfirmedNone =
    !isEmpty && !hasDirectorRole && entityAPDetails?.director_exists === false;
  const officerConfirmedNone =
    !isEmpty && !hasOfficerRole && entityAPDetails?.officer_exists === false;
  const bothConfirmedNone =
    isEmpty &&
    entityAPDetails?.director_exists === false &&
    entityAPDetails?.officer_exists === false;

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="t-flex t-flex-col t-rounded-xl t-border t-border-solid t-border-neutral-10 t-bg-surface t-p-5">
      <div className="t-mb-5 t-flex t-items-start t-justify-between">
        <div className="t-flex t-items-start t-gap-3">
          <span className="t-flex t-h-9 t-w-9 t-flex-shrink-0 t-items-center t-justify-center t-rounded t-bg-neutral-0 t-text-neutral-40">
            <PersonaIcon />
          </span>
          <div>
            <div className="t-text-subtext t-text-text-100">
              Directors & officers
            </div>
            <div className="t-text-body-sm t-text-text-30">
              Board members and company officers responsible for governance and
              management.
            </div>
          </div>
        </div>
      </div>

      {bothConfirmedNone ? (
        <NoAuthorisedPersonaTypeExist
          readOnly
          title="No officers & directors appointed"
          description={
            <>
              <div>
                You have recorded that no directors & officers have been
                appointed yet.
              </div>
              <div>You can add one at any time once assigned.</div>
            </>
          }
          icon={<EmptyStateWindIcon />}
        />
      ) : (
        <Async.Root
          isLoading={isLoading}
          isSuccess={isSuccess}
          isEmpty={isEmpty}
          isError={isError}
        >
          <Async.Empty>
            <NoAuthorisedPersonaTypeExist
              ctaText="Add new"
              title="No officers & directors added"
              description={
                <>
                  Add director details or confirm if there are none appointed
                  yet.
                </>
              }
              handleAdd={onAddPerson}
              handleConfirm={() => setConfirmVariant("both")}
              confirmCtaText="Not appointed yet"
              icon={<EmptyStateWindIcon />}
            />
          </Async.Empty>
          <Async.Success>
            <>
              <div className="t-flex t-h-full t-gap-2">
                <TableUI
                  size="regular"
                  table={activeTable}
                  onRowClick={onRowClick}
                  className="t-flex-1"
                  isHeaderCellUpperCase={false}
                  layout="fixed"
                />
                <Slider.Root open={openSlider} onOpenChange={setOpenSlider}>
                  <Slider.Content open={openSlider}>
                    {selectedPerson && (
                      <PersonSlider
                        person={selectedPerson}
                        key={selectedPerson.persona_autofill_id}
                        onEdit={() => {
                          setOpenSlider(false);
                          setEditingPerson(selectedPerson);
                        }}
                      />
                    )}
                  </Slider.Content>
                </Slider.Root>
              </div>

              {showMissingDirectorsBanner && (
                <div className="t-mt-3">
                  <Banner
                    type="warning"
                    icon={
                      <span className="t-flex t-shrink-0 t-self-start t-text-orange-80">
                        <MissingInfoWarningIcon />
                      </span>
                    }
                    className="t-border-orange-20 t-bg-orange-0"
                    titleClassName="t-text-text-100"
                    title="Director information missing"
                    description="We haven't recorded any directors for this company yet. Add a director or confirm that none have been appointed."
                    cta={
                      <Button
                        size="small"
                        customType="secondary"
                        onClick={() => setConfirmVariant("director")}
                      >
                        No directors appointed
                      </Button>
                    }
                  />
                </div>
              )}
              {showMissingOfficersBanner && (
                <div className="t-mt-3">
                  <Banner
                    type="warning"
                    icon={
                      <span className="t-flex t-shrink-0 t-self-start t-text-orange-80">
                        <MissingInfoWarningIcon />
                      </span>
                    }
                    className="t-border-orange-20 t-bg-orange-0"
                    titleClassName="t-text-text-100"
                    title="Officer information missing"
                    description="We haven't recorded any officers for this company yet. Add an officer or confirm that none have been appointed."
                    cta={
                      <Button
                        size="small"
                        customType="secondary"
                        onClick={() => setConfirmVariant("officer")}
                      >
                        No officers appointed
                      </Button>
                    }
                  />
                </div>
              )}
              {directorConfirmedNone && (
                <div className="t-mt-3">
                  <Banner
                    type="info"
                    title="Confirmed: No directors appointed"
                    description="You have recorded that no directors have been appointed yet. You can add one at any time using the add button above."
                  />
                </div>
              )}
              {officerConfirmedNone && (
                <div className="t-mt-3">
                  <Banner
                    type="info"
                    title="Confirmed: No officers appointed"
                    description="You have recorded that no officers have been appointed yet. You can add one at any time using the add button above."
                  />
                </div>
              )}

              {formerPeople.length > 0 && (
                <div className="t-mt-2">
                  <Button
                    type="button"
                    customType="link_neutral"
                    onClick={() => setShowFormer((prev) => !prev)}
                    className="t-py-3 !t-text-button-sm"
                  >
                    <span className="t-inline-flex t-items-center t-gap-1">
                      {showFormer ? "Hide" : "Show"} former directors & officers
                      ({formerPeople.length})
                      <span
                        className={
                          showFormer
                            ? "t-transform t-transition t-duration-200"
                            : "t-rotate-180 t-transform t-transition t-duration-200"
                        }
                      >
                        <SmallChevronToggleIcon />
                      </span>
                    </span>
                  </Button>
                  {showFormer && (
                    <TableUI
                      size="regular"
                      table={formerTable}
                      onRowClick={onRowClick}
                      sticky={false}
                      isHeaderCellUpperCase={false}
                      layout="fixed"
                    />
                  )}
                </div>
              )}
            </>
          </Async.Success>
        </Async.Root>
      )}

      <ConfirmNoDirectorOfficerModal
        open={confirmVariant !== null}
        variant={confirmVariant || "both"}
        onClose={() => setConfirmVariant(null)}
        onConfirm={() => {
          if (confirmVariant) {
            handleConfirmNone(confirmVariant);
          }
          setConfirmVariant(null);
        }}
      />
      {editingPersonWithShareholder && (
        <AddPersonModal
          isOpen
          close={() => setEditingPerson(null)}
          personToEdit={editingPersonWithShareholder}
          shareholderToEdit={editingShareholder}
        />
      )}
    </div>
  );
};
